import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../common/Table';
import Button from '../common/Button';
import Input from '../common/Input';
import Pagination from '../common/Pagination';
import Card from '../common/Card';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/product-list.css';

const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [pagination.page, category]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm) {
        searchProducts();
      } else {
        loadProducts();
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/products.php', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          category: category !== 'all' ? category : undefined
        }
      });
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories.php');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const searchProducts = async () => {
    if (!searchTerm) return;
    
    setLoading(true);
    try {
      const response = await api.get('/products.php/search', {
        params: { q: searchTerm }
      });
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to search products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/products.php/${id}`);
      loadProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const columns = [
    {
      key: 'product_name',
      label: 'Product',
      render: (value, row) => (
        <div className="product-cell">
          {row.image_url ? (
            <img src={row.image_url} alt={value} className="product-thumbnail" />
          ) : (
            <div className="product-thumbnail-placeholder">📦</div>
          )}
          <div>
            <div className="product-name">{value}</div>
            <div className="product-sku">{row.sku}</div>
          </div>
        </div>
      )
    },
    {
      key: 'category_name',
      label: 'Category'
    },
    {
      key: 'selling_price',
      label: 'Price',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'current_stock',
      label: 'Stock',
      render: (value, row) => (
        <span className={`stock-badge ${value <= row.min_stock_level ? 'low' : 'normal'}`}>
          {value} {row.unit}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Added',
      render: (value) => formatDate(value)
    }
  ];

  if (hasPermission('manage_products')) {
    columns.push({
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button 
            className="icon-btn edit"
            onClick={() => navigate(`/products/${row.id}`)}
            title="Edit"
          >
            ✏️ Edit
          </button>
          <button 
            className="icon-btn stock"
            onClick={() => navigate(`/products/stock/${row.id}`)}
            title="Adjust Stock"
          >
            📦 Restock
          </button>
          <button 
            className="icon-btn delete"
            onClick={() => handleDelete(row.id)}
            title="Delete"
          >
            🗑️ Delete
          </button>
        </div>
      )
    });
  }

  return (
    <div className="product-list">
      <Card>
        <div className="product-list-header">
          <h2>Products</h2>
          {hasPermission('manage_products') && (
            <div className="header-actions" style={{ display: 'flex', gap: '1rem' }}>
              <Button 
                variant="secondary"
                onClick={() => navigate('/products/bulk-upload')}
                icon="📥"
              >
                Bulk Upload
              </Button>
              <Button 
                variant="primary"
                onClick={() => navigate('/products/new')}
                icon="➕"
              >
                Add Product
              </Button>
            </div>
          )}
        </div>

        <div className="product-list-filters">
          <Input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon="🔍"
          />
          
          <select 
            value={category} 
            onChange={(e) => setCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.category_name}</option>
            ))}
          </select>
        </div>

        <Table
          columns={columns}
          data={products}
          loading={loading}
          onRowClick={(row) => navigate(`/products/${row.id}`)}
        />

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          onPageChange={(page) => setPagination({ ...pagination, page })}
        />
      </Card>
    </div>
  );
};

export default ProductList;